import React, { useState } from 'react';

/**
 * SubstitutionModal - Select incoming player from bench to substitute an outgoing player
 * 
 * @param {boolean} isOpen - Modal visibility
 * @param {object} outgoingPlayer - Player being substituted (pre-selected from quick actions)
 * @param {array} benchPlayers - Available substitute players
 * @param {function} onConfirm - Callback with (outgoingPlayer, incomingPlayer)
 * @param {function} onCancel - Close modal
 */
export const SubstitutionModal = ({ 
  isOpen, 
  outgoingPlayer, 
  benchPlayers, 
  onConfirm, 
  onCancel 
}) => {
  const [selectedIncoming, setSelectedIncoming] = useState(null);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedIncoming) return;
    onConfirm(outgoingPlayer, selectedIncoming);
    setSelectedIncoming(null);
  };

  const handleCancel = () => {
    setSelectedIncoming(null);
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold mb-4">Substitution</h2>
        
        {/* Outgoing Player */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Player OUT
          </label>
          <div className="p-3 bg-red-50 border-2 border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-gray-700">
                #{outgoingPlayer.jersey_number}
              </span>
              <span className="font-semibold text-gray-800">
                {outgoingPlayer.player.first_name} {outgoingPlayer.player.last_name}
              </span>
              {outgoingPlayer.is_captain && (
                <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded">
                  C
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Incoming Player Selection */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Player IN (from bench)
          </label>
          
          {benchPlayers.length === 0 ? (
            <div className="p-4 bg-gray-100 rounded-lg text-center text-gray-600">
              No substitute players available
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {benchPlayers.map((playerPos) => {
                const isSamePlayer = playerPos.player_id === outgoingPlayer.player_id;
                
                return (
                  <button
                    key={playerPos.id}
                    onClick={() => !isSamePlayer && setSelectedIncoming(playerPos)}
                    disabled={isSamePlayer}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      isSamePlayer
                        ? 'bg-gray-100 border-gray-300 opacity-50 cursor-not-allowed'
                        : selectedIncoming?.id === playerPos.id
                        ? 'bg-green-50 border-green-500'
                        : 'bg-white border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-gray-700">
                        #{playerPos.jersey_number}
                      </span>
                      <span className="font-semibold text-gray-800">
                        {playerPos.player.first_name} {playerPos.player.last_name}
                      </span>
                      {isSamePlayer && (
                        <span className="text-xs text-red-600 ml-auto">(Same player)</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedIncoming}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-semibold"
          >
            Confirm Substitution
          </button>
        </div>
      </div>
    </div>
  );
};
